import {
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { db } from '@/utils/firebaseConfig';
import { Racha, Participante, User } from '@/types/app';
import { logger } from '@/utils/logger';
import { validateRacha, validateParticipante, sanitizeString } from '@/utils/validation';
import {
  handleError,
  createSuccessResponse,
  withErrorHandling,
  requireAuth,
  requireResource,
  checkPermission
} from '@/utils/errorHandler';

const buildParticipantesIds = (participantes: Array<{ id?: string | null }> | undefined): string[] => {
  if (!participantes || participantes.length === 0) return [];
  const ids = new Set<string>();
  for (const participante of participantes) {
    if (participante?.id && typeof participante.id === 'string') {
      const id = participante.id.trim();
      if (id) ids.add(id);
    }
  }
  return Array.from(ids);
};

const requireDb = () => {
  if (!db) {
    throw new Error('Firebase não configurado neste build.');
  }
};

export const rachaService = {
  hasActiveListener: false,
  createRacha: withErrorHandling(async (rachaData: Partial<Racha>) => {
    requireDb();
    // Validação de autenticação
    requireAuth(rachaData.criadorId);

    // Validação robusta dos dados de entrada
    const validation = validateRacha(rachaData);
    if (!validation.isValid) {
      logger.warn('Invalid racha data provided', { errors: validation.errors, rachaData });
      throw new Error(`Dados inválidos: ${validation.errors.join(', ')}`);
    }

    const sanitizedData = validation.sanitizedValue;

    // Validação adicional de segurança
    if (!sanitizedData.nome || !rachaData.criadorId) {
      throw new Error('Nome e criadorId são obrigatórios');
    }

    // Sanitizar criadorId
    const criadorId = sanitizeString(rachaData.criadorId);
    if (!criadorId) {
      throw new Error('ID do criador inválido');
    }

    // Validação de limite de plano (Server-side check)
    const userRef = doc(db, 'usuarios', criadorId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data() as User;
      const isVitalicio = userData.plano === 'vitalicio';

      if (!isVitalicio) {
        // Verificar contagem de rachas ativos
        const q = query(
          collection(db, 'rachas'),
          where('criadorId', '==', criadorId),
          where('status', '==', 'ativo')
        );
        const snapshot = await getDocs(q);

        // Limite é 1 para gratuito (hardcoded aqui para segurança)
        if (snapshot.size >= 1) {
          throw new Error('Limite de rachas ativos atingido. Faça upgrade para criar ilimitados.');
        }
      }
    }

    // Cria o objeto com valores padrão seguros
    const participantes = sanitizedData.participantes || [];
    const rachaLimpo = {
      nome: sanitizedData.nome,
      emoji: sanitizedData.emoji || '💰',
      criadorId: criadorId,
      participantes,
      participantesIds: buildParticipantesIds(participantes),
      despesas: [],
      ultimaAtividade: 'Criado agora',
      status: 'ativo',
      gruposDeBolso: [],
      dataCriacao: new Date(),
      dataAtualizacao: new Date()
    };

    const docRef = await addDoc(collection(db, 'rachas'), rachaLimpo);
    return { id: docRef.id };
  }, 'createRacha', (error) => {
    // Custom error logger for this function if wrapper allows, otherwise use internal logging
    logger.error('Error creating racha in service', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
  }),

  updateRacha: async (rachaId: string, updates: Partial<Racha>) => {
    try {
      requireDb();
      // Validação do ID do racha
      const sanitizedRachaId = sanitizeString(rachaId);
      if (!sanitizedRachaId) {
        throw new Error('ID do racha inválido');
      }

      // Validação dos dados de atualização
      const validation = validateRacha(updates, true); // Partial validation
      if (!validation.isValid) {
        logger.warn('Invalid racha update data provided', { errors: validation.errors, updates });
        throw new Error(`Dados de atualização inválidos: ${validation.errors.join(', ')}`);
      }

      const rachaRef = doc(db, 'rachas', sanitizedRachaId);
      const updatesLimpos: Record<string, any> = {
        ...validation.sanitizedValue,
        dataAtualizacao: new Date()
      };

      // Ensure 'nome' is used if validated
      if (validation.sanitizedValue.nome) {
        updatesLimpos.nome = validation.sanitizedValue.nome;
      }

      // Processar despesas se existirem
      if (updates.despesas) {
        updatesLimpos.despesas = updates.despesas.map(d => {
          // Garantir que a data é válida
          let dataValida: Date;
          try {
            if (d.data instanceof Date) {
              dataValida = d.data;
            } else if (typeof d.data === 'string') {
              dataValida = new Date(d.data);
            } else if (d.data && typeof d.data === 'object' && 'seconds' in d.data) {
              // Tratar Timestamp do Firestore
              dataValida = new Date((d.data as any).seconds * 1000);
            } else {
              dataValida = new Date();
            }

            // Verificar se a data é válida
            if (isNaN(dataValida.getTime())) {
              logger.warn('Invalid date detected, using current date');
              dataValida = new Date();
            }
          } catch (error) {
            logger.warn('Error processing date, using current date', { error });
            dataValida = new Date();
          }

          return {
            id: d.id,
            titulo: d.titulo || '',
            valor: typeof d.valor === 'number' ? d.valor : 0,
            data: dataValida,
            categoria: d.categoria || {
              id: 'default',
              nome: 'Geral',
              emoji: '💰',
              cor: '#666666',
              icone: 'dollar'
            },
            pagoPorId: d.pagoPorId || '',
            pagoPorNome: d.pagoPorNome || '',
            modoDivisao: d.modoDivisao || 'igual',
            divisoes: (d.divisoes || []).map(div => ({
              usuarioId: div.usuarioId || '',
              valorDevido: typeof div.valorDevido === 'number' ? div.valorDevido : 0
            }))
          };
        });
      }

      // Processar outros campos específicos se necessário
      if (updates.participantes) {
        updatesLimpos.participantes = updates.participantes.map(p => ({
          id: p.id || '',
          nome: p.nome || '',
          email: p.email || '',
          foto: p.foto || '',
          chavePix: p.chavePix || null,
          grupoDeBolsoId: p.grupoDeBolsoId || null
        }));
        updatesLimpos.participantesIds = buildParticipantesIds(updatesLimpos.participantes);
      }
      if (updates.gruposDeBolso) {
        updatesLimpos.gruposDeBolso = updates.gruposDeBolso.map(g => ({
          id: g.id || '',
          nome: g.nome || ''
        }));
      }

      // Remover campos undefined/null antes de enviar para o Firestore
      Object.keys(updatesLimpos).forEach(key => {
        if (updatesLimpos[key] === undefined || updatesLimpos[key] === null) {
          delete updatesLimpos[key];
        }
      });

      await updateDoc(rachaRef, updatesLimpos);
      return { success: true };
    } catch (error: any) {
      logger.error('Error updating racha', { error });
      return { success: false, error: error.message };
    }
  },

  deleteRacha: withErrorHandling(async (rachaId: string, userId: string) => {
    requireDb();
    // Validação de autenticação
    requireAuth(userId);

    // Validação do ID do racha
    const sanitizedRachaId = sanitizeString(rachaId);
    requireResource(sanitizedRachaId, 'ID do racha');

    // Verificar se o racha existe e se o usuário tem permissão
    const rachaRef = doc(db, 'rachas', sanitizedRachaId);
    const rachaDoc = await getDoc(rachaRef);

    requireResource(rachaDoc.exists(), 'Racha');

    const rachaData = rachaDoc.data() as Racha;
    checkPermission(rachaData.criadorId === userId, 'Apenas o criador pode excluir o racha');

    await deleteDoc(rachaRef);
    return { deleted: true };
  }, 'deleteRacha'),

  getUserRachas: async (userId: string) => {
    try {
      requireDb();
      const q = query(collection(db, 'rachas'), where('criadorId', '==', userId));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Racha[];
      return { success: true, data };
    } catch (error: any) {
      logger.error('Error fetching user rachas', { error });
      return { success: false, error: error.message };
    }
  },

  getRachaById: async (rachaId: string) => {
    try {
      requireDb();
      const rachaRef = doc(db, 'rachas', rachaId);
      const rachaSnap = await getDoc(rachaRef);

      if (rachaSnap.exists()) {
        const data = { id: rachaSnap.id, ...rachaSnap.data() } as Racha;
        return { success: true, data };
      } else {
        return { success: false, error: 'Racha não encontrado' };
      }
    } catch (error: any) {
      logger.error('Error fetching racha by ID', { error });
      return { success: false, error: error.message };
    }
  },

  subscribeToUserRachas: (
    userId: string,
    callback: (rachas: Racha[]) => void
  ) => {
    requireDb();
    const q = query(collection(db, 'rachas'), where('criadorId', '==', userId));
    rachaService.hasActiveListener = true;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rachas = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Racha[];
      callback(rachas);
    });

    return () => {
      rachaService.hasActiveListener = false;
      unsubscribe();
    };
  },

  finalizarRacha: async (rachaId: string) => {
    try {
      logger.debug('Service: Iniciando finalização do racha', { rachaId });
      const result = await rachaService.updateRacha(rachaId, {
        status: 'finalizado',
        ultimaAtividade: 'Finalizado',
        dataFinalizacao: new Date()
      });
      logger.debug('Service: Resultado da finalização', { rachaId, result });
      return result;
    } catch (error: any) {
      logger.error('Service: Erro ao finalizar racha', { rachaId, error: error.message, stack: error.stack });
      return { success: false, error: error.message };
    }
  },

  reativarRacha: async (rachaId: string) => {
    try {
      logger.debug('Service: Iniciando reativação do racha', { rachaId });
      const result = await rachaService.updateRacha(rachaId, {
        status: 'ativo',
        ultimaAtividade: 'Reativado',
        dataFinalizacao: undefined
      });
      logger.debug('Service: Resultado da reativação', { rachaId, result });
      return result;
    } catch (error: any) {
      logger.error('Service: Erro ao reativar racha', { rachaId, error: error.message, stack: error.stack });
      return { success: false, error: error.message };
    }
  },

  addParticipanteToRacha: async (rachaId: string, participante: Participante) => {
    try {
      requireDb();
      // Validação do ID do racha
      const sanitizedRachaId = sanitizeString(rachaId);
      if (!sanitizedRachaId) {
        throw new Error('ID do racha inválido');
      }

      // Validação dos dados do participante
      const validation = validateParticipante(participante);
      if (!validation.isValid) {
        logger.warn('Invalid participant data provided', { errors: validation.errors, participante });
        throw new Error(`Dados do participante inválidos: ${validation.errors.join(', ')}`);
      }

      const sanitizedParticipante = validation.sanitizedValue;

      // Obter dados atuais do racha
      const rachaRef = doc(db, 'rachas', sanitizedRachaId);
      const rachaDoc = await getDoc(rachaRef);

      if (!rachaDoc.exists()) {
        return { success: false, error: 'Racha não encontrado' };
      }

      const rachaData = rachaDoc.data() as Racha;

      // Verificar se o participante já existe
      const participanteExiste = rachaData.participantes.some(p => p.id === sanitizedParticipante.id);
      if (participanteExiste) {
        return { success: false, error: 'Participante já está no racha' };
      }

      // Adicionar o novo participante à lista existente
      const participantesAtualizados = [...rachaData.participantes, sanitizedParticipante];

      const updates = {
        participantes: participantesAtualizados,
        participantesIds: buildParticipantesIds(participantesAtualizados),
        ultimaAtividade: 'Agora'
      };

      await updateDoc(rachaRef, updates);
      return { success: true };
    } catch (error: any) {
      logger.error('Error adding participant', { error });
      return { success: false, error: error.message };
    }
  },

  removeParticipanteFromRacha: async (rachaId: string, participanteId: string) => {
    try {
      requireDb();
      // Obter dados atuais do racha
      const rachaRef = doc(db, 'rachas', rachaId);
      const rachaDoc = await getDoc(rachaRef);

      if (!rachaDoc.exists()) {
        return { success: false, error: 'Racha não encontrado' };
      }

      const rachaData = rachaDoc.data() as Racha;

      // Verificar se o participante não é o criador
      if (rachaData.criadorId === participanteId) {
        return { success: false, error: 'Não é possível remover o criador do racha' };
      }

      // Remover participante da lista
      const participantesAtualizados = rachaData.participantes.filter(p => p.id !== participanteId);

      // Remover participante das divisões de despesas e recalcular valores
      const despesasAtualizadas = (rachaData.despesas || []).map(despesa => {
        const divisoesFiltradas = despesa.divisoes.filter(div => div.usuarioId !== participanteId);

        // Se a despesa foi paga pelo participante removido, manter como está
        // mas remover das divisões
        if (despesa.pagoPorId === participanteId) {
          return {
            ...despesa,
            divisoes: divisoesFiltradas
          };
        }

        // Recalcular divisões se necessário (modo igual)
        if (despesa.modoDivisao === 'igual' && divisoesFiltradas.length > 0) {
          const valorPorPessoa = despesa.valor / divisoesFiltradas.length;
          const divisoesRecalculadas = divisoesFiltradas.map(div => ({
            ...div,
            valorDevido: valorPorPessoa
          }));

          return {
            ...despesa,
            divisoes: divisoesRecalculadas
          };
        }

        return {
          ...despesa,
          divisoes: divisoesFiltradas
        };
      });

      // Remover participante dos vínculos de bolso
      const participantesComGruposAtualizados = participantesAtualizados.map(p =>
        p.grupoDeBolsoId ? p : { ...p, grupoDeBolsoId: null }
      );

      // Atualizar documento
      await updateDoc(rachaRef, {
        participantes: participantesComGruposAtualizados,
        participantesIds: buildParticipantesIds(participantesComGruposAtualizados),
        despesas: despesasAtualizadas,
        ultimaAtividade: 'Participante removido',
        dataAtualizacao: new Date()
      });

      return { success: true };
    } catch (error: any) {
      logger.error('Error removing participant', { error });
      return { success: false, error: error.message };
    }
  }
};
